import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function RepairingAsphaltRoofPage() {
  useEffect(() => {
    document.title = 'Asphalt Roof Repair NWA | Roof Repair Near Me | Fayetteville AR Maintenance';
    
    // Set meta tags
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert asphalt roof repair in Northwest Arkansas. Fast roof repair near me, leak repair, shingle replacement & maintenance. Serving Fayetteville, Bentonville, Rogers & Springdale. Free estimates!');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'asphalt roof repair, roof repair near me, roof maintenance, shingle repair, leak repair, Fayetteville roof repair, NWA roofing maintenance');
    }
  }, []);

  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white pt-32 pb-20">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional Asphalt Roof Repair in Northwest Arkansas
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Fast Leak Repair | Shingle Replacement | Preventive Maintenance | Expert Service
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:4795551234" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-lg">
                <i className="ri-phone-fill mr-2"></i>
                Call: (479) 555-1234
              </a>
              <a href="#quote-form" className="bg-white hover:bg-gray-100 text-blue-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-lg">
                Get Free Estimate
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Introduction */}
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Expert Asphalt Roof Repair Services Near You
                </h2>
                <p className="text-lg text-gray-700 mb-4">
                  Not every roofing problem requires a complete replacement. NWA Roofing specializes in professional asphalt roof repair services throughout Fayetteville, Bentonville, Rogers, Springdale, and Bella Vista. Our expert repairs extend your roof's lifespan and protect your home at a fraction of replacement cost.
                </p>
                <p className="text-lg text-gray-700 mb-4">
                  With over 25 years of experience repairing asphalt shingle roofs in Northwest Arkansas, we quickly identify problems and provide lasting solutions. From minor leak repairs to extensive shingle replacement, our certified technicians deliver quality workmanship that stands the test of time.
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  Don't let small problems become major expenses. Early detection and prompt repair of roof damage saves money and prevents interior damage to your home. We offer free inspections to assess your roof's condition and recommend the most cost-effective solutions.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center text-green-600">
                    <i className="ri-checkbox-circle-fill text-2xl mr-2"></i>
                    <span className="font-semibold">Fast Service</span>
                  </div>
                  <div className="flex items-center text-green-600">
                    <i className="ri-checkbox-circle-fill text-2xl mr-2"></i>
                    <span className="font-semibold">Quality Repairs</span>
                  </div>
                  <div className="flex items-center text-green-600">
                    <i className="ri-checkbox-circle-fill text-2xl mr-2"></i>
                    <span className="font-semibold">Fair Pricing</span>
                  </div>
                  <div className="flex items-center text-green-600">
                    <i className="ri-checkbox-circle-fill text-2xl mr-2"></i>
                    <span className="font-semibold">Free Estimates</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://public.readdy.ai/ai/img_res/2e6257ce64c482c75e97ea8d77b940ea.jpg"
                  alt="Professional asphalt roof repair Fayetteville AR shingle repair near me"
                  className="rounded-lg shadow-2xl w-full h-auto object-cover"
                />
              </div>
            </div>

            {/* Common Repairs */}
            <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 md:p-12 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                Common Asphalt Roof Repairs We Handle
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-drop-fill text-3xl text-blue-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Leak Repair</h3>
                  <p className="text-gray-700 mb-4">
                    Fast, effective leak repair to stop water intrusion and prevent damage to your home's interior, insulation, and structure.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                      <span>Locate leak source</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                      <span>Seal and waterproof</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                      <span>Prevent future leaks</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-layout-grid-fill text-3xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Shingle Replacement</h3>
                  <p className="text-gray-700 mb-4">
                    Replace missing, damaged, or worn shingles to restore your roof's protection and appearance without full replacement.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                      <span>Match existing shingles</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                      <span>Secure installation</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                      <span>Seamless appearance</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-flashlight-fill text-3xl text-purple-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Flashing Repair</h3>
                  <p className="text-gray-700 mb-4">
                    Repair or replace damaged flashing around chimneys, vents, skylights, and valleys to prevent water infiltration.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                      <span>Chimney flashing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                      <span>Vent pipe sealing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                      <span>Valley repairs</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-windy-fill text-3xl text-orange-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Wind Damage Repair</h3>
                  <p className="text-gray-700 mb-4">
                    Repair wind-damaged shingles, secure loose materials, and restore your roof's wind resistance after storms.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                      <span>Secure lifted shingles</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                      <span>Replace torn sections</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                      <span>Reinforce weak areas</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-contrast-drop-2-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Granule Loss Repair</h3>
                  <p className="text-gray-700 mb-4">
                    Address areas with excessive granule loss that compromise shingle protection and accelerate aging.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-red-600 mr-2 mt-1"></i>
                      <span>Assess damage extent</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-red-600 mr-2 mt-1"></i>
                      <span>Replace affected shingles</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-red-600 mr-2 mt-1"></i>
                      <span>Prevent further loss</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-tools-fill text-3xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Preventive Maintenance</h3>
                  <p className="text-gray-700 mb-4">
                    Regular maintenance to identify and address minor issues before they become major problems requiring costly repairs.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                      <span>Annual inspections</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                      <span>Minor repairs</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                      <span>Extend roof life</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Signs You Need Repair */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
                Signs Your Asphalt Roof Needs Repair
              </h2>
              <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
                Don't ignore these warning signs - early repair prevents costly damage to your Northwest Arkansas home.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-blue-600">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    <i className="ri-drop-line text-blue-600 mr-2"></i>
                    Water Stains
                  </h3>
                  <p className="text-gray-700">
                    Brown or yellow stains on ceilings or walls indicate active or past leaks requiring immediate attention.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-blue-600">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    <i className="ri-layout-grid-line text-blue-600 mr-2"></i>
                    Missing Shingles
                  </h3>
                  <p className="text-gray-700">
                    Gaps in your roof where shingles are missing expose underlayment and deck to weather damage.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-blue-600">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    <i className="ri-contrast-drop-2-line text-blue-600 mr-2"></i>
                    Granules in Gutters
                  </h3>
                  <p className="text-gray-700">
                    Excessive granules washing into gutters indicate advanced shingle wear and reduced protection.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-blue-600">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    <i className="ri-arrow-up-down-line text-blue-600 mr-2"></i>
                    Curling Shingles
                  </h3>
                  <p className="text-gray-700">
                    Shingles curling at edges or cupping in the middle signal aging and vulnerability to wind damage.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-blue-600">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    <i className="ri-contrast-2-line text-blue-600 mr-2"></i>
                    Dark Streaks
                  </h3>
                  <p className="text-gray-700">
                    Dark streaks or discoloration may indicate algae growth, moisture retention, or shingle deterioration.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-blue-600">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    <i className="ri-sun-line text-blue-600 mr-2"></i>
                    Daylight Through Roof
                  </h3>
                  <p className="text-gray-700">
                    Visible daylight through roof boards in your attic indicates holes or gaps requiring immediate repair.
                  </p>
                </div>
              </div>
            </div>

            {/* Repair vs Replacement */}
            <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white rounded-2xl p-8 md:p-12 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                Repair vs. Replacement: Making the Right Choice
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4 text-green-400">
                    <i className="ri-tools-fill mr-2"></i>
                    When to Repair
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Roof is less than 15-20 years old</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Damage is localized to specific areas</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Only a few shingles are damaged or missing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Leak is isolated and easily accessible</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Overall roof condition is good</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Budget constraints favor repair</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-4 text-red-400">
                    <i className="ri-home-gear-fill mr-2"></i>
                    When to Replace
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-close-line text-red-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Roof is over 20-25 years old</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-line text-red-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Widespread damage across entire roof</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-line text-red-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Multiple leaks in different areas</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-line text-red-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Sagging or structural issues present</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-line text-red-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Extensive granule loss throughout</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-line text-red-400 text-xl mr-3 mt-1"></i>
                      <span className="text-blue-100">Frequent repairs becoming costly</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 text-center">
                <p className="text-lg text-blue-100 mb-4">
                  Not sure which option is right for you? We provide honest assessments during free inspections, recommending the most cost-effective solution for your situation.
                </p>
                <a href="#quote-form" className="inline-block bg-white hover:bg-gray-100 text-blue-900 px-8 py-3 rounded-lg font-semibold transition-all">
                  Get Free Assessment
                </a>
              </div>
            </div>

            {/* Maintenance Tips */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                Asphalt Roof Maintenance Tips
              </h2>
              <div className="bg-blue-50 rounded-2xl p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      <i className="ri-calendar-check-line text-blue-600 mr-2"></i>
                      Regular Maintenance Tasks
                    </h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-fill text-blue-600 mr-2 mt-1"></i>
                        <span>Schedule annual professional inspections</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-fill text-blue-600 mr-2 mt-1"></i>
                        <span>Clean gutters and downspouts regularly</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-fill text-blue-600 mr-2 mt-1"></i>
                        <span>Remove debris, leaves, and branches promptly</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-fill text-blue-600 mr-2 mt-1"></i>
                        <span>Trim overhanging tree branches</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-fill text-blue-600 mr-2 mt-1"></i>
                        <span>Check for and address moss or algae growth</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-fill text-blue-600 mr-2 mt-1"></i>
                        <span>Ensure proper attic ventilation</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      <i className="ri-alert-line text-red-600 mr-2"></i>
                      What to Avoid
                    </h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                        <span>Walking on your roof unnecessarily</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                        <span>Using pressure washers on shingles</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                        <span>Ignoring small leaks or damage</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                        <span>DIY repairs without proper knowledge</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                        <span>Allowing ice dams to form in winter</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                        <span>Delaying professional inspections</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                Asphalt Roof Repair FAQs
              </h2>
              <div className="space-y-4 max-w-4xl mx-auto">
                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    How much does asphalt roof repair cost in Northwest Arkansas?
                    <i className="ri-arrow-down-s-line text-2xl text-blue-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    Asphalt roof repair costs vary widely based on the extent of damage, accessibility, materials needed, and labor required. Minor repairs like replacing a few shingles may cost a few hundred dollars, while more extensive repairs involving flashing, underlayment, or multiple areas cost more. We provide free estimates with transparent pricing so you know exactly what to expect. Many repairs are covered by homeowners insurance if damage is storm-related. Our goal is providing cost-effective solutions that extend your roof's life without unnecessary expenses.
                  </p>
                </details>

                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    How long do asphalt roof repairs last?
                    <i className="ri-arrow-down-s-line text-2xl text-blue-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    When done properly with quality materials, asphalt roof repairs can last many years - often for the remaining life of your roof. The longevity depends on the type of repair, quality of materials used, installation quality, and ongoing maintenance. Our professional repairs use premium materials and proven techniques to ensure lasting results. We stand behind our work with warranties on both materials and workmanship. Regular maintenance and prompt attention to new issues help maximize the lifespan of repairs and your entire roofing system.
                  </p>
                </details>

                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    Can I repair my asphalt roof myself?
                    <i className="ri-arrow-down-s-line text-2xl text-blue-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    While some minor repairs might seem simple, we strongly recommend professional roof repair for several reasons: safety concerns (falls from roofs cause serious injuries), proper diagnosis of underlying issues, correct repair techniques that don't void warranties, access to quality materials, and insurance requirements. DIY repairs often miss hidden damage, use improper techniques, or create new problems. Professional repairs ensure the job is done right the first time, protecting your home and your investment. Our affordable repair services provide peace of mind and lasting results.
                  </p>
                </details>

                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    How quickly can you repair my roof?
                    <i className="ri-arrow-down-s-line text-2xl text-blue-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    Most asphalt roof repairs in Northwest Arkansas can be completed in one day, with minor repairs taking just a few hours. We prioritize emergency repairs like active leaks to prevent further damage. After your free inspection, we provide a timeline for your specific repair needs. Weather conditions may affect scheduling, but we work efficiently to complete repairs as quickly as possible while maintaining quality standards. For emergency situations, we offer expedited service to protect your home. Contact us today for fast, professional roof repair service.
                  </p>
                </details>

                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    Will insurance cover my roof repair?
                    <i className="ri-arrow-down-s-line text-2xl text-blue-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    Homeowners insurance typically covers roof repairs when damage results from covered events like storms, hail, wind, or falling trees. Damage from normal wear and tear or lack of maintenance usually isn't covered. We help document damage for insurance claims, work with adjusters, and provide detailed estimates that meet insurance requirements. If you're unsure about coverage, we recommend calling your insurance company after discovering damage. We have extensive experience with insurance claims throughout Northwest Arkansas and can guide you through the process to maximize your coverage.
                  </p>
                </details>

                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    How often should I have my asphalt roof inspected?
                    <i className="ri-arrow-down-s-line text-2xl text-blue-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    We recommend professional roof inspections at least once per year, ideally in spring or fall. Additional inspections should be scheduled after severe weather events like hail storms, high winds, or heavy snow. Regular inspections identify minor issues before they become major problems, extending your roof's lifespan and preventing costly repairs. Our free inspections provide peace of mind and help you budget for any necessary maintenance. For older roofs (15+ years), twice-yearly inspections help catch age-related issues early. Preventive maintenance is always more cost-effective than emergency repairs.
                  </p>
                </details>
              </div>
            </div>

            {/* Service Areas */}
            <div className="bg-blue-50 rounded-2xl p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                Roof Repair Service Areas
              </h2>
              <p className="text-lg text-gray-700 text-center mb-8 max-w-3xl mx-auto">
                NWA Roofing provides expert asphalt roof repair services throughout Northwest Arkansas.
              </p>
              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="bg-white rounded-lg p-6 text-center shadow-md">
                  <i className="ri-map-pin-fill text-4xl text-blue-600 mb-3"></i>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">Fayetteville</h3>
                  <p className="text-gray-600">Fast roof repair & maintenance services</p>
                </div>
                <div className="bg-white rounded-lg p-6 text-center shadow-md">
                  <i className="ri-map-pin-fill text-4xl text-blue-600 mb-3"></i>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">Bentonville</h3>
                  <p className="text-gray-600">Expert leak repair & shingle replacement</p>
                </div>
                <div className="bg-white rounded-lg p-6 text-center shadow-md">
                  <i className="ri-map-pin-fill text-4xl text-blue-600 mb-3"></i>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">Rogers</h3>
                  <p className="text-gray-600">Professional asphalt roof repairs</p>
                </div>
                <div className="bg-white rounded-lg p-6 text-center shadow-md">
                  <i className="ri-map-pin-fill text-4xl text-blue-600 mb-3"></i>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">Springdale</h3>
                  <p className="text-gray-600">Quality roof repair near you</p>
                </div>
                <div className="bg-white rounded-lg p-6 text-center shadow-md">
                  <i className="ri-map-pin-fill text-4xl text-blue-600 mb-3"></i>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">Bella Vista</h3>
                  <p className="text-gray-600">Reliable roofing maintenance services</p>
                </div>
                <div className="bg-white rounded-lg p-6 text-center shadow-md">
                  <i className="ri-map-pin-fill text-4xl text-blue-600 mb-3"></i>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">All NWA</h3>
                  <p className="text-gray-600">Serving all Northwest Arkansas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-form" className="py-16 bg-gradient-to-br from-slate-900 to-blue-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Get Your Free Roof Repair Estimate
              </h2>
              <p className="text-xl text-blue-100">
                Contact us today for fast, professional asphalt roof repair services!
              </p>
            </div>
            <QuoteForm />
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Related Roofing Services
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Link to="/services/roof-inspection" className="group bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl p-6 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <i className="ri-search-eye-line text-4xl text-blue-600 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                Free Roof Inspections
              </h3>
              <p className="text-gray-600 mb-4">
                Comprehensive inspections to identify all repair needs and potential issues.
              </p>
              <span className="text-blue-600 font-semibold group-hover:underline">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>

            <Link to="/services/asphalt-shingle-roof" className="group bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl p-6 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <i className="ri-stack-fill text-4xl text-blue-600 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                Asphalt Shingle Roofing
              </h3>
              <p className="text-gray-600 mb-4">
                Learn about premium asphalt shingle options and complete roof replacement.
              </p>
              <span className="text-blue-600 font-semibold group-hover:underline">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>

            <Link to="/services/storm-damage" className="group bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl p-6 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <i className="ri-thunderstorms-line text-4xl text-blue-600 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                Storm Damage Repair
              </h3>
              <p className="text-gray-600 mb-4">
                Expert storm damage assessment and repair with insurance assistance.
              </p>
              <span className="text-blue-600 font-semibold group-hover:underline">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}